/**
 * Shared allowlist HTML sanitizer — pure string functions, Node-safe (no DOM).
 *
 * One implementation for every sink that turns translation data into HTML:
 * core's HTMLSerializer/auto-translator, the editor text sinks, the snapshot
 * viewer, react `<Trans>`, and WordPress TS. The WordPress PHP port must
 * mirror `SAFE_TAGS`/`SAFE_ATTRS` exactly (see improvement-plan §4 item 2).
 *
 * Threat model: translation values (and their `raw:` styles) come from the
 * server/KV and may be attacker-controlled — they are NOT guaranteed to be in
 * canonical form. Everything here is allowlist-based: unknown tags are
 * rejected, unknown attributes dropped, URL schemes checked after entity
 * decoding.
 *
 * @module content/sanitizer/HTMLSanitizer
 */

import { extractTagName, readRawStyle, serializeOpeningTag } from '../canonical-tag.js';
import type { ContentUnit } from '../types.js';

/**
 * Inline formatting elements allowed inside translated content. Block
 * structure lives outside ContentUnit (block-level pipeline), so anything
 * that isn't inline text formatting is rejected outright.
 */
export const SAFE_TAGS = new Set([
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'cite', 'code', 'del', 'dfn',
  'em', 'i', 'img', 'ins', 'kbd', 'mark', 'q', 's', 'samp', 'small',
  'span', 'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr',
]);

/**
 * Flat attribute allowlist (plus `data-*`/`aria-*` prefixes, handled in
 * `sanitizeOpeningTag`). Kept flat — not per-tag — so the PHP mirror stays
 * trivial; an allowed attribute on the wrong tag is inert.
 */
export const SAFE_ATTRS = new Set([
  'alt', 'cite', 'class', 'datetime', 'dir', 'height', 'href', 'id',
  'lang', 'loading', 'rel', 'role', 'src', 'target', 'title', 'width',
]);

/** Attributes whose values are URLs and must pass `sanitizeUrl`. */
const URL_ATTRS = new Set(['href', 'src', 'cite']);

/**
 * CSS properties allowed through an inline `style` attribute.
 *
 * RISK NOTE — why `style` never passes through verbatim: with zero
 * JavaScript, an attacker who controls a style value can render a
 * full-viewport overlay (`position:fixed;inset:0` → pixel-perfect fake
 * login UI on the customer's real domain), fire tracking/exfiltration
 * beacons via `url(...)`, or hide and deface content. Translation data is
 * attacker-writable in our threat model, so `sanitizeStyle` re-parses the
 * declarations and keeps only the properties below: cosmetic text/font
 * properties whose value grammar cannot fetch a URL and cannot reposition
 * or restack the element (no `position`/`inset`/`display`/`z-index`/
 * `opacity`/`transform`, no `background` shorthand). A smuggled `url(...)`
 * inside an allowed property is doubly inert: the value is rejected here,
 * and these properties ignore URL values anyway.
 *
 * CONTINUOUS DEVELOPMENT: this allowlist is intentionally minimal and is
 * expected to grow deliberately as real sites surface legitimate inline
 * styling the strict set drops (tracked as a WATCH item in
 * IMPLEMENTATION.md). When extending it: (1) never add a property that
 * accepts `url()`/`image()`/`element()` values or that affects layout,
 * positioning, or stacking; (2) add both hostile and benign cases to
 * html-sanitizer.test.ts; (3) mirror the change in the WordPress PHP port.
 */
export const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'font-style',
  'font-weight', 'letter-spacing', 'line-height', 'text-decoration',
  'text-decoration-color', 'text-decoration-line', 'text-decoration-style',
  'text-transform', 'vertical-align', 'white-space', 'word-spacing',
]);

/**
 * Value-level rejections inside an otherwise-allowed declaration: URL/code
 * carriers, and backslashes because CSS escape sequences (`\75\72\6c`)
 * can spell `url(` past a textual check.
 */
const DANGEROUS_CSS_VALUE =
  /url\s*\(|expression\s*\(|image-set\s*\(|element\s*\(|paint\s*\(|javascript:|\\|[<>{}@]/i;

const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

/**
 * The named entities that matter for scheme smuggling (`&colon;`, `&Tab;`)
 * plus the basic set. Names are case-sensitive, as in HTML.
 */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  colon: ':', semi: ';', sol: '/', bsol: '\\',
  Tab: '\t', NewLine: '\n',
};

/**
 * Decode entities exactly once — mirroring the single decode a browser does
 * when parsing an attribute value out of HTML.
 */
function decodeEntities(value: string): string {
  return value.replace(
    /&(?:#[xX]([0-9a-fA-F]+);?|#(\d+);?|([a-zA-Z]+);)/g,
    (full, hex: string, dec: string, name: string) => {
      if (hex) return String.fromCodePoint(parseInt(hex, 16));
      if (dec) return String.fromCodePoint(parseInt(dec, 10));
      return NAMED_ENTITIES[name] ?? full;
    },
  );
}

/** HTML-escape text for safe use in element content or attribute values. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Validate a URL attribute value. Returns the value unchanged when safe,
 * or `null` when it must be dropped.
 *
 * Entity-decodes first, then strips the control/whitespace characters
 * browsers ignore inside a scheme (`jav\tascript:`), then requires any
 * explicit scheme to be on the allowlist. Scheme-less (relative) URLs pass.
 */
export function sanitizeUrl(value: string): string | null {
  const decoded = decodeEntities(value);
  // eslint-disable-next-line no-control-regex -- stripping C0 controls/space/DEL is the point
  const probe = decoded.replace(/[\u0000-\u0020\u007f]/g, '').toLowerCase();
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(probe);
  if (!scheme) return value;
  return SAFE_SCHEMES.has(scheme[1]) ? value : null;
}

/**
 * Sanitize an inline `style` attribute value by re-parsing its declarations
 * and keeping only `SAFE_STYLE_PROPS` with clean values (see the risk note
 * on `SAFE_STYLE_PROPS`). Returns the rebuilt declaration list in canonical
 * `prop: value; prop: value` form, or `null` when nothing survives — the
 * caller then drops the attribute entirely.
 */
export function sanitizeStyle(value: string): string | null {
  return filterCssDeclarations(value, true);
}

/**
 * Relaxed CSS profile for the snapshot viewer: keeps ANY standard property
 * (a snapshot is a full captured page — `position:fixed` navbars and layout
 * styles are legitimate and must render) but still rejects value-level
 * dangers: `url(...)` beacons, `expression()`, CSS escape smuggling. The
 * residual risk (page-authored overlay styles) is accepted for snapshots:
 * the PM is deliberately viewing arbitrary captured pages, and the editor
 * UI itself lives in a Shadow DOM host stacked above the snapshot root.
 */
export function sanitizeCssDeclarations(value: string): string | null {
  return filterCssDeclarations(value, false);
}

function filterCssDeclarations(
  value: string,
  requirePropAllowlist: boolean,
): string | null {
  const decoded = decodeEntities(value);
  const kept: string[] = [];
  for (const declaration of decoded.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) continue;
    const prop = declaration.slice(0, colon).trim().toLowerCase();
    const val = declaration.slice(colon + 1).trim();
    if (!/^[a-z-]+$/.test(prop)) continue; // also blocks --custom-prop indirection
    if (requirePropAllowlist && !SAFE_STYLE_PROPS.has(prop)) continue;
    if (val === '' || DANGEROUS_CSS_VALUE.test(val)) continue;
    kept.push(`${prop}: ${val}`);
  }
  return kept.length > 0 ? kept.join('; ') : null;
}

/**
 * Attribute scanner for a (possibly hostile, non-canonical) opening tag.
 * Handles double-quoted, single-quoted, unquoted, and bare boolean attrs.
 */
const ATTR_PATTERN =
  /([^\s=/>"']+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;

/**
 * Sanitize a raw opening tag (`raw:` style payload). Returns the canonical
 * rebuilt tag with only allowlisted attributes, or `null` when the element
 * itself is not allowed — callers must then drop the tag entirely (emit the
 * indexed marker verbatim / skip the element).
 */
export function sanitizeOpeningTag(rawOpen: string): string | null {
  if (!rawOpen.startsWith('<')) return null;
  const tagName = extractTagName(rawOpen);
  if (!/^[a-z][a-z0-9-]*$/.test(tagName) || !SAFE_TAGS.has(tagName)) {
    return null;
  }

  // The slice between `<tagname` and the closing `>` (or end of string —
  // hostile input may be truncated).
  const end = rawOpen.endsWith('/>')
    ? rawOpen.length - 2
    : rawOpen.endsWith('>')
      ? rawOpen.length - 1
      : rawOpen.length;
  const attrSource = rawOpen.slice(1 + tagName.length, end);

  const attrs: Record<string, string> = {};
  for (const match of attrSource.matchAll(ATTR_PATTERN)) {
    const name = match[1].toLowerCase();
    const value = match[2] ?? match[3] ?? match[4] ?? '';
    if (name in attrs) continue; // first occurrence wins, as in HTML
    if (name === 'style') {
      // Never verbatim — re-parsed against the CSS property allowlist.
      const cleanStyle = sanitizeStyle(value);
      if (cleanStyle !== null) attrs[name] = cleanStyle;
      continue;
    }
    const allowed =
      SAFE_ATTRS.has(name) ||
      /^data-[a-z0-9-]*$/.test(name) ||
      /^aria-[a-z0-9-]*$/.test(name);
    if (!allowed) continue;
    if (URL_ATTRS.has(name)) {
      if (sanitizeUrl(value) === null) continue;
    }
    attrs[name] = value;
  }

  return serializeOpeningTag(tagName, attrs);
}

/**
 * Sanitize a ContentUnit at an ingest boundary: run every `raw:` style
 * through {@link sanitizeOpeningTag} so no dangerous element/attribute/scheme
 * enters the translation store. Render-time sinks (HTMLSerializer etc.)
 * sanitize again — this is the store-side half, protecting every other
 * consumer of stored units (server, MT/TM, exports, the WP PHP port).
 *
 * `text` is never modified, so `generateAutoKey(unit.text)` is unaffected —
 * key parity across surfaces holds even for hostile input. A rejected
 * element's `raw:` style is dropped entirely; the indexed tag left in the
 * text is emitted by serializers as an inert marker. Returns the SAME object
 * when nothing changed, so benign units round-trip identically.
 */
export function sanitizeContentUnit(unit: ContentUnit): ContentUnit {
  let changed = false;
  const styles: Record<string, string[]> = {};

  for (const [index, styleList] of Object.entries(unit.styles)) {
    const raw = readRawStyle(styleList);
    if (raw === null) {
      styles[index] = styleList; // no raw tag → nothing to sanitize
      continue;
    }
    const safe = sanitizeOpeningTag(raw);
    if (safe === null) {
      // Element rejected — drop the raw style (keep any non-raw entries).
      changed = true;
      const rest = styleList.filter((s) => !s.startsWith('raw:'));
      if (rest.length > 0) styles[index] = rest;
    } else if (safe === raw) {
      styles[index] = styleList;
    } else {
      changed = true;
      styles[index] = styleList.map((s) => (s.startsWith('raw:') ? `raw:${safe}` : s));
    }
  }

  return changed ? { text: unit.text, styles } : unit;
}
